/**
 * @fileoverview Wallet Modal Component
 * Modal for selecting and connecting to different Zcash wallets
 */

import React, { useState, lazy, Suspense } from 'react';
import { X, Shield, Lock, Zap } from 'lucide-react';
import { useZcashWallet } from '../../hooks/useZcashWallet';
import { ZcashWalletService } from '../../services/zcashWallet';
import type { SupportedWalletType, Winbit32StorageOptions } from '../../services/zcashWallet';
import ManualWalletModal from './ManualWalletModal';
import Winbit32WalletModal from './Winbit32WalletModal';
import WalletUnlockModal from './WalletUnlockModal';

// Lazy: the co-signing modal pulls in the Orchard FROST engine, relay client
// and WASM loader. Keep that out of the eager bundle until a user picks it.
const CosignWalletModal = lazy(() => import('./CosignWalletModal'));

interface WalletModalProps {
	isOpen: boolean;
	onClose: () => void;
}

const WalletModal: React.FC<WalletModalProps> = ({ isOpen, onClose }) => {
	const { connect, unlock, isConnecting, connectionError } = useZcashWallet();
	const [showManualModal, setShowManualModal] = useState(false);
	const [showWinbit32Modal, setShowWinbit32Modal] = useState(false);
	const [showUnlockModal, setShowUnlockModal] = useState(false);
	const [showCosignModal, setShowCosignModal] = useState(false);

	// Branch the signing-option cards: cosign opens its own modal; the others
	// go through the seed-based connect flow.
	const handleOptionSelect = (key: string) => {
		if (key === 'cosign') {
			setShowCosignModal(true);
			return;
		}
		void handleWalletSelect(key as SupportedWalletType);
	};

	// Handle wallet selection
	const handleWalletSelect = async (walletType: SupportedWalletType) => {
		if (walletType === 'manual') {
			setShowManualModal(true);
			return;
		}
		if (walletType === 'winbit32') {
			// Decide which flow to show based on what (if anything) is stored:
			//   encrypted record  → unlock prompt (just the password)
			//   session plaintext → connect straight away
			//   nothing stored    → enter/generate a seed
			const svc = ZcashWalletService.getInstance();
			const info = svc.getStoredMnemonicInfo();
			if (info.exists && info.encrypted) {
				setShowUnlockModal(true);
				return;
			}
			if (info.exists) {
				try {
					await connect('winbit32');
					onClose();
				} catch (error) {
					console.error('Winbit32 wallet connection error:', error);
				}
				return;
			}
			setShowWinbit32Modal(true);
			return;
		}

		try {
			await connect(walletType);
			onClose();
		} catch (error) {
			// Error handled by hook
			console.error('Wallet connection error:', error);
		}
	};

	// Handle manual wallet connection
	const handleManualConnect = async (shieldedAddress: string, transparentAddress: string) => {
		try {
			const walletService = ZcashWalletService.getInstance();
			walletService.setManualAddresses(shieldedAddress, transparentAddress);
			await connect('manual');
			setShowManualModal(false);
			onClose();
		} catch (error) {
			console.error('Manual wallet connection error:', error);
			throw error;
		}
	};

	const handleWinbit32Connect = async (mnemonic: string, options: Winbit32StorageOptions) => {
		try {
			const walletService = ZcashWalletService.getInstance();
			await walletService.setWinbit32Mnemonic(mnemonic, options);
			await connect('winbit32');
			setShowWinbit32Modal(false);
			onClose();
		} catch (error) {
			console.error('Winbit32 wallet connection error:', error);
			throw error;
		}
	};

	const handleUnlock = async (password: string) => {
		// Throws on wrong password so the unlock modal can surface it.
		await unlock(password);
		setShowUnlockModal(false);
		onClose();
	};

	const handleForgetSaved = () => {
		ZcashWalletService.getInstance().clearWinbit32Mnemonic();
		setShowUnlockModal(false);
		setShowWinbit32Modal(true);
	};

	// Connection options. Note: there is no browser-plugin / app "wallet" to
	// connect to. Order matters — this is the on-screen order. Co-signing
	// first (it is the preferred option, so it gets a plain-language
	// explanation for people who have never met it), then the in-browser
	// seed, and "use your own external wallet" last.
	const walletConfigs: Record<string, { name: string; description: string; icon: string; features: string[]; downloadUrl: string | null }> = {
		cosign: {
			name: 'Co-sign with a Winbit32 vault (recommended)',
			description: 'The key is split across devices, like a safe that needs two keys. This browser loads one share (a .wult file) and a second device approves each send — so no single device, and never this website, can spend on its own.',
			icon: '🛡️',
			features: ['Multi-sig (FROST)', 'Orchard Shielded', 'QR Pairing'],
			downloadUrl: null
		},
		winbit32: {
			name: 'Sign in this browser (seed phrase)',
			description: 'Paste a Zcash seed phrase. The shielded transaction is built, signed and broadcast by this browser; the phrase never leaves this device.',
			icon: '🌿',
			features: ['Shielded Sends', 'Memo Support', 'Orchard Notes'],
			downloadUrl: null,
		},
		manual: {
			name: 'Use your own external Zcash wallet',
			description: 'Get the memo + address + amount to paste into Zashi, YWallet, Zingo… any shielded-memo-capable wallet. Nothing is signed on this site.',
			icon: '📝',
			features: ['Any Wallet', 'Memo Required', 'Copy & Paste'],
			downloadUrl: null
		}
	};

	if (!isOpen) return null;

	// Closing the cosign modal: if a share was loaded (i.e. we're now connected)
	// the picker has done its job and should get out of the way; otherwise the
	// user backed out, so fall back to the picker.
	const handleCosignClose = () => {
		setShowCosignModal(false);
		if (ZcashWalletService.getInstance().isConnected()) onClose();
	};

	return (
		<>
			{/* The signing-option picker. Hidden while the cosign modal is open so
			    loading a `.wult` share doesn't leave this box stacked behind it. */}
			{!showCosignModal && (
				<div className="fixed inset-0 z-50 overflow-y-auto">
					{/* Backdrop */}
					<div className="fixed inset-0 bg-black/70 transition-opacity" onClick={onClose} />

					{/* Modal — themed to match the bottom Winbit32 wallet bar. */}
					<div className="flex items-center justify-center min-h-screen p-4">
						<div className="relative bg-slate-900 text-slate-100 rounded-xl shadow-2xl ring-1 ring-white/10 max-w-md w-full max-h-[90vh] overflow-hidden">
							{/* Header */}
							<div className="bg-gradient-to-r from-slate-800 to-slate-900 border-b border-white/10 px-6 py-4">
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-2.5">
										<img src="/winbit32-logo.png" alt="Winbit32" width={28} height={28} draggable={false} className="rounded" />
										<h2 className="text-xl font-bold text-white">Connect your account</h2>
									</div>
									<button
										onClick={onClose}
										className="text-slate-300 hover:text-white transition-colors"
										aria-label="Close modal"
									>
										<X className="w-6 h-6" />
									</button>
								</div>
								<p className="text-slate-400 text-sm mt-2">
									There is no plugin to install. Choose how your shielded inscriptions get signed.
								</p>
							</div>

							{/* Content */}
							<div className="p-6 max-h-96 overflow-y-auto">
								{/* Connection Error */}
								{connectionError && (
									<div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
										<p className="text-sm text-red-200">{connectionError}</p>
									</div>
								)}

								{/* Wallet Options */}
								<div className="space-y-3">
									{Object.entries(walletConfigs).map(([walletType, config]) => {
										const isDisabled = isConnecting;

										return (
											<div
												key={walletType}
												className={`relative border rounded-lg p-4 transition-all cursor-pointer ${
													isDisabled
														? 'border-white/10 bg-white/5 cursor-not-allowed'
														: 'border-white/10 bg-slate-800/60 hover:border-emerald-400/50 hover:bg-slate-800'
												}`}
												onClick={() => !isDisabled && handleOptionSelect(walletType)}
											>
												<div className="flex items-start space-x-3">
													<div className="text-2xl">{config.icon}</div>
													<div className="flex-1 min-w-0">
														<div className="flex items-center justify-between">
															<h3 className="text-sm font-semibold text-slate-100">
																{config.name}
															</h3>
														</div>
														<p className="text-xs text-slate-400 mt-1">
															{config.description}
														</p>

														{/* Features */}
														<div className="flex flex-wrap gap-1 mt-2">
															{config.features.map((feature, index) => (
																<span
																	key={index}
																	className="inline-flex items-center text-xs bg-white/5 text-slate-300 ring-1 ring-white/10 px-2 py-1 rounded"
																>
																	{feature}
																</span>
															))}
														</div>
													</div>
												</div>

												{/* Loading State */}
												{isConnecting && (
													<div className="absolute inset-0 bg-slate-900/75 flex items-center justify-center rounded-lg">
														<div className="animate-spin w-5 h-5 border-2 border-emerald-400 border-t-transparent rounded-full" />
													</div>
												)}
											</div>
										);
									})}
								</div>

								{/* Features Section */}
								<div className="mt-6 pt-6 border-t border-white/10">
									<h4 className="text-sm font-medium text-slate-100 mb-3">
										What Secresea actually does
									</h4>
									<div className="space-y-2">
										<div className="flex items-center space-x-2">
											<Shield className="w-4 h-4 text-emerald-400" />
											<span className="text-xs text-slate-300">Builds the shielded inscription memo</span>
										</div>
										<div className="flex items-center space-x-2">
											<Lock className="w-4 h-4 text-zcash-400" />
											<span className="text-xs text-slate-300">
												Your seed phrase stays in this browser — it is never sent to any server
											</span>
										</div>
										<div className="flex items-center space-x-2">
											<Zap className="w-4 h-4 text-emerald-400" />
											<span className="text-xs text-slate-300">Scans the chain so your mint shows up automatically</span>
										</div>
									</div>
								</div>

								{/* Security Notice */}
								<div className="mt-4 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 space-y-2">
									<p className="text-xs text-amber-200">
										<strong>Heads up:</strong> If you choose "Sign in this browser", the seed
										phrase is used locally to construct the PCZT and never leaves this device.
										By default it's kept only for this browser <strong>session</strong> — it
										survives page reloads but is cleared when you close the tab. You can
										optionally protect it with a password, or
										opt in to remembering it on this device — in which case it's stored
										<strong> encrypted</strong> with that password.
									</p>
									<p className="text-xs text-amber-200">
										If you'd rather not paste a full seed into a browser, co-signing keeps
										the key split across devices, and the external-wallet option never
										touches a key at all.
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Manual Wallet Modal */}
			<ManualWalletModal
				isOpen={showManualModal}
				onClose={() => setShowManualModal(false)}
				onConnect={handleManualConnect}
			/>

			<Winbit32WalletModal
				isOpen={showWinbit32Modal}
				onClose={() => setShowWinbit32Modal(false)}
				onConnect={handleWinbit32Connect}
			/>

			<WalletUnlockModal
				isOpen={showUnlockModal}
				onClose={() => setShowUnlockModal(false)}
				onUnlock={handleUnlock}
				onUseDifferentSeed={() => {
					setShowUnlockModal(false);
					setShowWinbit32Modal(true);
				}}
				onForget={handleForgetSaved}
			/>

			{showCosignModal && (
				<Suspense fallback={null}>
					<CosignWalletModal
						isOpen={showCosignModal}
						onClose={handleCosignClose}
					/>
				</Suspense>
			)}
		</>
	);
};

export default WalletModal;